<!--
  NetCheckoutCredential -- what a shopper gives to place a Net Checkout order
  (Net Checkout PIN proposal 3.4 / 3.5, MVP-8).

  Two things, both rendered only while Net is the chosen tender:

    1. The store's Net PIN, once per session. With no pass the shopper sees a
       PIN field and Verify; a right PIN mints a server-side net pass (24
       hours) and the field is replaced by a "PIN accepted" line. The pass
       lives on the server, so a second order, a reload, or a remount finds
       it with GET net-pass: the browser never holds it.
    2. The Net Checkout terms, ticked on EVERY Net order. The text and its
       version come from the server (GET net-pass), so the version sent with
       the order is never hardcoded here.

  On a store whose Net credential is the store password (P1-1), item 1 is
  the pass the shopper already got by entering the store password at the
  storefront wall: GET net-pass says `credential: "store_password"`, no PIN
  field is shown, and without the pass the shopper is told to enter the store
  password again (there is no PIN to offer). The terms are unchanged.

  The component emits `state` ({ ready, termsVersion }): `ready` is true only
  with a pass AND the terms ticked, which is what enables Place Order. The
  parent also calls two methods when the order build refuses in place:
  `reopenPin()` (403 net_pin_required: the pass expired or the PIN was
  rotated) and `reloadTerms()` (409 net_terms_stale / 400 net_terms_required).

  The PIN lives only in this component's state: never logged, cleared on a
  right PIN, on a wrong one, on a store change and on unmount.

  Behavioural tests: `__tests__/net-checkout-credential.spec.js`.
-->
<template>
  <div class="net-checkout-credential mt-3">
    <div v-if="loading" class="net-cred-hint" data-testid="co-net-loading">
      Checking Net Checkout...
    </div>

    <template v-else>
      <template v-if="storePasswordMode">
        <div
          v-if="held"
          class="net-cred-accepted"
          role="status"
          aria-live="polite"
          data-testid="co-net-store-password-ok"
        >
          <v-icon color="success" size="small" class="mr-1">mdi-check-circle</v-icon>
          You entered the store password, so no PIN is needed.
        </div>
        <div
          v-else
          class="net-cred-error"
          role="alert"
          data-testid="co-net-store-password-missing"
        >
          Net Checkout on this store uses the store password. Please reload
          the store and enter its password again, or pay by card.
        </div>
      </template>

      <div
        v-else-if="held"
        class="net-cred-accepted"
        role="status"
        aria-live="polite"
        data-testid="co-net-pin-accepted"
      >
        <v-icon color="success" size="small" class="mr-1">mdi-check-circle</v-icon>
        PIN accepted
      </div>

      <div v-else>
        <div class="net-cred-hint mb-2" data-testid="co-net-pin-intro">
          Enter this store's Net Checkout PIN to pay by Net Checkout.
        </div>
        <div class="d-flex align-start">
          <v-text-field
            v-model="pin"
            type="password"
            label="Net Checkout PIN"
            autocomplete="new-password"
            density="compact"
            hide-details
            :disabled="busy"
            style="max-width: 220px"
            data-testid="co-net-pin"
            @keyup.enter="verify"
          />
          <v-btn
            class="ml-2"
            color="primary"
            variant="flat"
            size="small"
            style="text-transform: none"
            :disabled="!pin || busy"
            :loading="busy"
            data-testid="co-net-pin-verify"
            @click="verify"
          >
            Verify
          </v-btn>
        </div>
        <div
          v-if="pinError"
          class="net-cred-error mt-1"
          role="alert"
          data-testid="co-net-pin-error"
        >
          {{ pinError }}
        </div>
      </div>

      <div v-if="termsVersion" class="mt-2">
        <v-checkbox
          v-model="termsAccepted"
          density="compact"
          hide-details
          data-testid="co-net-terms"
          @update:model-value="onTermsToggle"
        >
          <template #label>
            <span data-testid="co-net-terms-text">{{ termsText }}</span>
          </template>
        </v-checkbox>
        <div
          v-if="termsNotice"
          class="net-cred-hint mt-1"
          data-testid="co-net-terms-notice"
        >
          {{ termsNotice }}
        </div>
      </div>
      <div
        v-else-if="loadFailed"
        class="net-cred-error"
        role="alert"
        data-testid="co-net-unavailable"
      >
        Net Checkout could not be loaded. Please pay by card, or try again in
        a moment.
      </div>
    </template>
  </div>
</template>

<script>
import { SUPPRESS_ERROR_TOAST } from "@/interceptors/authResponseInterceptor";
import {
  storefrontNetPassUrl,
  storefrontNetPinVerifyUrl,
} from "./storefrontStoreApi";

// This component shows its own errors, so the global toast stays quiet.
const QUIET = { [SUPPRESS_ERROR_TOAST]: true };

const WRONG_PIN = "That PIN is not right.";
const TOO_MANY =
  "Too many attempts. Wait a few minutes and try again, or pay by card.";
const GENERIC = "Something went wrong. Please try again, or pay by card.";
const PIN_REQUIRED = "Please enter the Net Checkout PIN to place this order.";
const TERMS_CHANGED =
  "The Net Checkout terms have changed. Please review and accept them to place this order.";

export default {
  name: "NetCheckoutCredential",

  props: {
    storeId: { type: [Number, String], required: true },
  },

  emits: ["state"],

  data() {
    return {
      loading: true,
      loadFailed: false,
      held: false,
      // "pin" or "store_password" (P1-1), from GET net-pass.
      credential: "pin",
      termsVersion: "",
      termsText: "",
      termsAccepted: false,
      termsNotice: "",
      // Component state only: never logged, never stored elsewhere.
      pin: "",
      pinError: "",
      busy: false,
    };
  },

  computed: {
    storePasswordMode() {
      return this.credential === "store_password";
    },
  },

  watch: {
    // A different store is a different credential.
    storeId() {
      this.pin = "";
      this.pinError = "";
      this.termsAccepted = false;
      this.termsNotice = "";
      this.load();
    },
  },

  mounted() {
    this.load();
  },

  beforeUnmount() {
    this.pin = "";
    // Take the "ready" with us: the parent must not keep it for a remount.
    this.$emit("state", { ready: false, termsVersion: null });
  },

  methods: {
    onTermsToggle(accepted) {
      if (accepted) this.termsNotice = "";
      this.emitState();
    },

    emitState() {
      this.$emit("state", {
        ready:
          !this.loading &&
          this.held &&
          this.termsAccepted &&
          !!this.termsVersion,
        termsVersion: this.termsVersion || null,
      });
    },

    /** Read the pass state and the current terms (GET net-pass). */
    async load() {
      this.loading = true;
      this.loadFailed = false;
      // Not ready while the pass and terms are being read.
      this.emitState();
      try {
        const resp = await this.$axios.get(
          storefrontNetPassUrl(this.storeId),
          QUIET
        );
        const data = resp?.data || {};
        this.held = data.held === true;
        this.credential =
          data.credential === "store_password" ? "store_password" : "pin";
        const nextVersion = data.terms_version || "";
        // A changed version voids an acceptance of the old words.
        if (nextVersion !== this.termsVersion) this.termsAccepted = false;
        this.termsVersion = nextVersion;
        this.termsText = data.terms_text || "";
        this.loadFailed = !nextVersion;
      } catch {
        // Not knowing is not a pass: stay unready, say so, card still works.
        this.held = false;
        this.termsVersion = "";
        this.termsAccepted = false;
        this.loadFailed = true;
      } finally {
        this.loading = false;
        this.emitState();
      }
    },

    async verify() {
      const pin = this.pin;
      if (!pin || this.busy) return;
      this.busy = true;
      this.pinError = "";
      try {
        await this.$axios.post(
          storefrontNetPinVerifyUrl(this.storeId),
          { pin },
          QUIET
        );
        this.held = true;
      } catch (err) {
        this.held = false;
        this.pinError = this.messageFor(err);
      } finally {
        // Right or wrong, the typed PIN does not stay in the field.
        this.pin = "";
        this.busy = false;
        this.emitState();
      }
    },

    messageFor(err) {
      const status = err?.response?.status;
      if (status === 429) return TOO_MANY;
      if (status === 400) return WRONG_PIN;
      // 403 net_checkout_unavailable carries its own plain-words message.
      return err?.response?.data?.error || GENERIC;
    },

    /** 403 net_pin_required: the pass is gone; ask for the PIN again here. */
    reopenPin() {
      this.held = false;
      this.pin = "";
      this.pinError = PIN_REQUIRED;
      this.emitState();
    },

    /** 409 net_terms_stale / 400 net_terms_required: refetch and re-ask. */
    async reloadTerms() {
      this.termsAccepted = false;
      this.termsNotice = TERMS_CHANGED;
      await this.load();
    },
  },
};
</script>

<style scoped>
.net-cred-hint {
  font-size: 14px;
  color: #555;
}

.net-cred-error {
  font-size: 14px;
  color: #b00020;
}

.net-cred-accepted {
  font-size: 14px;
}
</style>
